import type { Message } from '@/hooks/useConversationMessages';
import {
  getUnsupportedMessagePreview,
  isUnsupportedMessageType,
  UNSUPPORTED_MESSAGE_PREVIEW,
} from './unsupportedMessage';


export function isOwnMessage(msg: Message | null | undefined): boolean {
  if (!msg) return false;
  const raw = (msg as { is_inbound?: unknown }).is_inbound;
  const inbound = raw === true || raw === 1 || raw === '1';
  return !inbound;
}

function pickReadableText(obj: Record<string, unknown>): string {
  const pick = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : '');
  return (
    pick(obj.text) ||
    pick(obj.bodyText) ||   // whatsapp_flow / interactive body
    pick(obj.body) ||
    pick(obj.caption) ||    // media caption
    pick(obj.title) ||
    pick(obj.ctaLabel)      // last resort (flow button label)
  );
}

export function getMessageText(msg: Message | null | undefined): string {
  if (!msg) return '';
  const ct = (msg as { content_text?: unknown }).content_text;
  if (typeof ct === 'string') {
    if (ct.trimStart().startsWith('{')) {
      try {
        const parsed = JSON.parse(ct) as Record<string, unknown>;
        const readable = pickReadableText(parsed);
        if (readable) return readable;
        if (typeof parsed.type === 'string') return '';
        return ct;
      } catch { /* not JSON, return as-is */ }
    }
    return ct;
  }
  if (ct && typeof ct === 'object') {
    const obj = ct as Record<string, unknown>;
    const readable = pickReadableText(obj);
    if (readable) return readable;
    const media = (msg as { media_url?: unknown }).media_url;
    return typeof media === 'string' ? media : '';
  }
  return '';
}

export function getMediaUrl(msg: Message | null | undefined): string {
  if (!msg) return '';
  const m = msg as { media_url?: unknown; mediaUrl?: unknown };
  const u = typeof m.media_url === 'string' ? m.media_url
    : typeof m.mediaUrl === 'string' ? m.mediaUrl
      : '';
  return u;
}

const COPYABLE_IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|bmp)(\?|#|$)/i;

export function isCopyableImageUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string' || /\s/.test(url)) return false;
  let path: string;
  try { path = new URL(url).pathname; } catch { return false; }
  return COPYABLE_IMAGE_EXT_RE.test(path);
}

export function resolveCopyImageUrl(msg: Message | null | undefined): string {
  if (!msg) return '';
  const mediaUrl = getMediaUrl(msg);
  const mt = String(
    (msg as { media_type?: unknown }).media_type ??
    (msg as { mediaType?: unknown }).mediaType ??
    (msg as { message_type?: unknown }).message_type ??
    '',
  ).toLowerCase();

  if (mediaUrl && (mt === 'image' || mt === 'sticker' || isCopyableImageUrl(mediaUrl))) {
    return mediaUrl;
  }

  const ct = (msg as { content_text?: unknown }).content_text;
  if (typeof ct === 'string') {
    const trimmed = ct.trim();
    if (isCopyableImageUrl(trimmed)) return trimmed;
  }
  return '';
}

export function getStructuredMessageType(msg: Message | null | undefined): string {
  if (!msg) return '';
  const mt = String((msg as { message_type?: unknown }).message_type ?? '').toLowerCase();
  const ct = (msg as { content_text?: unknown }).content_text;
  const readType = (o: Record<string, unknown>): string =>
    typeof o.type === 'string' ? o.type.toLowerCase() : '';
  if (typeof ct === 'string' && ct.trimStart().startsWith('{')) {
    try { return readType(JSON.parse(ct) as Record<string, unknown>) || mt; } catch { /* not JSON */ }
  } else if (ct && typeof ct === 'object') {
    return readType(ct as Record<string, unknown>) || mt;
  }
  return mt;
}

export function isTemplateLikeMessage(msg: Message | null | undefined): boolean {
  const t = getStructuredMessageType(msg);
  return t === 'template' || t === 'interactive' || t === 'whatsapp_flow' || t === 'flow';
}

export type ParsedWhatsAppTemplatePayload = {
  templateName: string;
  language?: string;
  variables?: Record<string, string>;
  headerMediaUrl?: string;
  carouselMediaUrls?: Record<string, string>;
};

function coerceTemplateVariables(templateData: Record<string, unknown>): Record<string, string> | undefined {
  let variables: Record<string, string> | undefined;

  const asVars = templateData.variables;
  if (asVars && typeof asVars === 'object' && !Array.isArray(asVars)) {
    variables = {};
    for (const [k, v] of Object.entries(asVars as Record<string, unknown>)) {
      variables[k] = typeof v === 'string' ? v : String((v as any)?.text ?? (v as any)?.value ?? v ?? '');
    }
  }

  if ((!variables || Object.keys(variables).length === 0) && Array.isArray(templateData.parameters)) {
    variables = {};
    (templateData.parameters as unknown[]).forEach((param, index) => {
      const value =
        typeof param === 'string'
          ? param
          : String((param as any)?.text ?? (param as any)?.value ?? param ?? '');
      variables![String(index + 1)] = value;
    });
  }

  if (
    (!variables || Object.keys(variables).length === 0) &&
    templateData.namedParameters &&
    typeof templateData.namedParameters === 'object' &&
    !Array.isArray(templateData.namedParameters)
  ) {
    variables = {};
    for (const [k, v] of Object.entries(templateData.namedParameters as Record<string, unknown>)) {
      variables[k] = typeof v === 'string' ? v : String((v as any)?.text ?? (v as any)?.value ?? v ?? '');
    }
  }

  if (
    (!variables || Object.keys(variables).length === 0) &&
    Array.isArray(templateData.components)
  ) {
    const bodyComponent = (templateData.components as any[]).find(
      (c) =>
        (c?.type === 'body' || c?.type === 'BODY') &&
        Array.isArray(c.parameters),
    );
    if (bodyComponent?.parameters) {
      const hasNamed = bodyComponent.parameters.some((p: any) => p?.parameter_name);
      variables = {};
      if (hasNamed) {
        bodyComponent.parameters.forEach((param: any) => {
          if (param?.parameter_name) {
            variables![param.parameter_name] = String(
              param.text ?? param.value ?? param ?? '',
            );
          }
        });
      } else {
        bodyComponent.parameters.forEach((param: any, index: number) => {
          variables![String(index + 1)] = String(
            param?.text ?? param?.value ?? param ?? '',
          );
        });
      }
    }
  }

  return variables && Object.keys(variables).length > 0 ? variables : undefined;
}

export function parseWhatsAppTemplatePayload(
  msg: Message | null | undefined,
): ParsedWhatsAppTemplatePayload | null {
  if (!msg) return null;
  const ct = (msg as { content_text?: unknown }).content_text;
  let templateData: Record<string, unknown> | null = null;
  if (typeof ct === 'string' && ct.trimStart().startsWith('{')) {
    try {
      const parsed = JSON.parse(ct) as Record<string, unknown>;
      if (parsed && typeof parsed === 'object') templateData = parsed;
    } catch {
      return null;
    }
  } else if (ct && typeof ct === 'object') {
    templateData = ct as Record<string, unknown>;
  }
  if (!templateData) return null;

  const type = String(templateData.type ?? (msg as any).message_type ?? '').toLowerCase();
  const templateName = String(
    templateData.templateName ?? templateData.template_name ?? templateData.name ?? '',
  ).trim();
  if (type !== 'template' && !templateName) return null;
  if (!templateName) return null;

  const language =
    typeof templateData.language === 'string'
      ? templateData.language
      : typeof templateData.lang === 'string'
        ? templateData.lang
        : undefined;
  const headerMediaUrl =
    typeof templateData.headerMediaUrl === 'string'
      ? templateData.headerMediaUrl
      : typeof templateData.header_media_url === 'string'
        ? templateData.header_media_url
        : undefined;

  const rawCards = templateData.carouselMediaUrls;
  const carouselMediaUrls =
    rawCards && typeof rawCards === 'object' && !Array.isArray(rawCards)
      ? Object.fromEntries(
          Object.entries(rawCards as Record<string, unknown>).filter(
            (e): e is [string, string] => typeof e[1] === 'string',
          ),
        )
      : undefined;

  return {
    templateName,
    language,
    variables: coerceTemplateVariables(templateData),
    headerMediaUrl,
    carouselMediaUrls,
  };
}

export function getForwardContentPreview(msg: Message | null | undefined): string {
  if (!msg) return '';
  const tpl = parseWhatsAppTemplatePayload(msg);
  if (tpl) {
    return tpl.language
      ? `📋 Template: ${tpl.templateName} (${tpl.language})`
      : `📋 Template: ${tpl.templateName}`;
  }
  const text = getMessageText(msg);
  if (text) return text.length > 140 ? `${text.substring(0, 140)}…` : text;

  const mediaType = String((msg as { media_type?: unknown }).media_type ?? '').toLowerCase();
  if (mediaType === 'image') return '📷 Image';
  if (mediaType === 'video') return '🎥 Video';
  if (mediaType === 'audio') return '🎵 Audio';
  if (mediaType === 'document') return '📄 Document';
  if (mediaType) return '📎 Media';

  const ct = (msg as { content_text?: unknown }).content_text;
  if (typeof ct === 'string' && ct.trimStart().startsWith('{')) {
    const structured = getStructuredMessageType(msg);
    if (structured) return `[${structured}]`;
  }
  if (typeof ct === 'string' && ct.trim()) {
    return ct.length > 140 ? `${ct.substring(0, 140)}…` : ct;
  }
  return 'Message';
}

export interface AgentTypingState {
  burstCount: number;
  lastKeystrokeAt: number;
}

export const AGENT_TYPING_BURST_THRESHOLD = 3;
export const AGENT_TYPING_IDLE_RESET_MS = 3000;

export function evaluateAgentTypingFire(
  prev: AgentTypingState,
  value: string,
  now: number,
): { nextState: AgentTypingState; shouldFire: boolean } {
  if (value.length === 0) {
    return {
      nextState: { burstCount: 0, lastKeystrokeAt: 0 },
      shouldFire: false,
    };
  }
  const idleGap = prev.lastKeystrokeAt === 0
    ? Infinity
    : now - prev.lastKeystrokeAt;
  const baseCount = idleGap > AGENT_TYPING_IDLE_RESET_MS ? 0 : prev.burstCount;
  const nextCount = baseCount + 1;
  return {
    nextState: { burstCount: nextCount, lastKeystrokeAt: now },
    shouldFire: nextCount >= AGENT_TYPING_BURST_THRESHOLD,
  };
}

export function buildReplyQuotePrefix(
  replyingTo: Message | null | undefined,
  userBody: string,
  contactName?: string | null,
): string {
  if (!replyingTo) return userBody;
  const quotedName = isOwnMessage(replyingTo) ? 'You' : (contactName || 'Contact');
  const quotedText = getMessageText(replyingTo);
  const snippet = quotedText && quotedText.length > 140
    ? `${quotedText.substring(0, 140)}…`
    : (quotedText || '[media]');
  return `> ${quotedName}: ${snippet}\n\n${userBody}`;
}

/**
 * The words for an Instagram live-location share whose card carries Meta's
 * `statusTitle`/`statusText`. Meta writes those in the SENDER's Instagram
 * language ("लाईव्ह लोकेशन" from a Marathi phone), so they are never shown —
 * they only mark the row as a share rather than a place. Live vs. ended comes
 * from what the webhook stored: a map or a point means the share was live.
 */
export function instagramShareStatus(isLive: boolean): { title: string; text: string } {
  return isLive
    ? { title: 'Live location', text: 'Sharing live location' }
    : { title: 'Location expired', text: 'Stopped sharing' };
}

/** Same rule as above, read off a stored location body. */
export function instagramShareStatusFromBody(loc: Record<string, unknown> | null | undefined): { title: string; text: string } | null {
  if (!loc || (!loc.statusTitle && !loc.statusText)) return null;
  const lat = Number(loc.latitude);
  const lng = Number(loc.longitude);
  const hasPoint = loc.latitude != null && loc.longitude != null && Number.isFinite(lat) && Number.isFinite(lng);
  return instagramShareStatus(hasPoint || (typeof loc.mapImageUrl === 'string' && !!loc.mapImageUrl));
}

export interface ConversationPreviewMessage {
  message_type?: string | null;
  messageType?: string | null;
  content_text?: unknown;
  is_inbound?: unknown;
  text?: unknown;
  body?: unknown;
  caption?: unknown;
  metadata?: unknown;
}

const previewStr = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const previewObj = (v: unknown): Record<string, unknown> | undefined =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;
const previewParse = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};
const clip = (text: string, max: number) => (text.length > max ? text.substring(0, max) + '...' : text);

export function formatConversationPreview(message: ConversationPreviewMessage | null | undefined): string {
  if (!message) return 'No messages yet';

  const isInbound = Boolean(message.is_inbound) || message.is_inbound === 1 || message.is_inbound === '1';
  const msgType = (message.message_type || message.messageType || '').toLowerCase();
  const contentText = previewStr(message.content_text);

  if (isUnsupportedMessageType(msgType)) {
    return getUnsupportedMessagePreview(message) ?? UNSUPPORTED_MESSAGE_PREVIEW;
  }
  if (msgType === 'story_mention') return '📖 Mentioned you in their story';
  if (msgType === 'story_reply') {
    const text = (contentText || '').trim();
    return text ? `💬 Story reply: ${clip(text, 40)}` : '💬 Replied to your story';
  }
  if (msgType === 'product') return '🛍️ Product';
  if (msgType === 'product_list') return '🛍️ Product catalogue';
  const locationPreview = (loc: Record<string, unknown>) => {
    const label = (previewStr(loc.name) || previewStr(loc.address) || instagramShareStatusFromBody(loc)?.title || '').trim();
    return label ? `📍 ${clip(label, 40)}` : '📍 Location';
  };
  if (msgType === 'location') {
    return locationPreview(previewObj(previewParse(contentText || '{}')) || {});
  }

  if (contentText && contentText.trim() !== '') {
    if (
      contentText === 'Flow completed' ||
      contentText === 'Form submitted' ||
      contentText === 'flow' ||
      (contentText.includes('type') && contentText.includes('form_submission'))
    ) {
      return isInbound ? '✅ Form received' : '✅ Form sent';
    }

    const parsed = previewObj(previewParse(contentText));
    if (parsed) {
      const interactiveData = previewObj(parsed.interactiveData);
      const snapshot = previewObj(parsed.snapshot);

      if (parsed.type === 'form_submission' || interactiveData?.type === 'form_submission') {
        return '✅ Form sent';
      }

      if (
        parsed.latitude != null &&
        parsed.longitude != null &&
        Number.isFinite(Number(parsed.latitude)) &&
        Number.isFinite(Number(parsed.longitude))
      ) {
        return locationPreview(parsed);
      }

      if (parsed.type === 'template') {
        const body = previewStr(snapshot?.body);
        if (body) return clip(body, 50);
        const templateName = previewStr(parsed.templateName);
        if (templateName) return `📋 Template: ${templateName}`;
      }

      if (parsed.type === 'whatsapp_flow') {
        const text = previewStr(parsed.bodyText) || previewStr(parsed.ctaLabel) || previewStr(parsed.flowName) || 'Interactive Flow';
        return `📝 ${text}`;
      }

      if (parsed.type === 'interactive' || parsed.interactiveData) {
        if (parsed.interactive_type === 'product_catalog' || interactiveData?.type === 'product_list') {
          return '🛍️ Product catalogue';
        } else if (interactiveData?.type === 'product') {
          return '🛍️ Product';
        } else if (parsed.interactive_type === 'flow' || interactiveData?.type === 'whatsapp_flow') {
          const flowName = previewStr(parsed.flow_name) || previewStr(interactiveData?.flowName) || 'Interactive Form';
          return `📝 ${flowName}`;
        } else if (previewStr(parsed.bodyText)) {
          return previewStr(parsed.bodyText) as string;
        } else if (previewStr(parsed.text)) {
          const text = previewStr(parsed.text) as string;
          if (text.toLowerCase() === 'flow') return '✅ Form sent';
          return clip(text, 50);
        }
      }

      const readable =
        previewStr(parsed.bodyText) ||
        previewStr(snapshot?.body) ||
        previewStr(parsed.text) ||
        previewStr(parsed.display_text) ||
        previewStr(parsed.title);
      if (readable) {
        if (readable.toLowerCase() === 'flow') return isInbound ? '✅ Form received' : '✅ Form sent';
        return clip(readable, 50);
      }

      if (parsed.type === 'interactive') return isInbound ? '✅ Form received' : '✅ Form sent';
    }

    if (contentText.toLowerCase().includes('interactive message')) {
      const type = message.message_type || message.messageType || '';
      if (type.toLowerCase() === 'interactive' || type === 'unknown' || !type) {
        return isInbound ? '✅ Form received' : '✅ Form sent';
      }
    }

    return clip(contentText, 50);
  }

  const messageType = message.message_type || message.messageType || 'text';
  switch (messageType.toLowerCase()) {
    case 'image':
      return '📷 Image';
    case 'video':
      return '🎥 Video';
    case 'audio':
      return '🎵 Audio';
    case 'document':
      return '📄 Document';
    case 'template':
      return '📋 Template message';
    case 'button':
      return '🔘 Button message';
    case 'interactive':
      return isInbound ? '✅ Form received' : '✅ Form sent';
    case 'product':
      return '🛍️ Product';
    case 'product_list':
      return '🛍️ Product catalogue';
    case 'location':
      return '📍 Location';
    case 'sticker':
      return '🎨 Sticker';
    default: {
      const fallback = previewStr(message.text) || previewStr(message.body) || previewStr(message.caption);
      return fallback ? clip(fallback, 50) : '💬 Message';
    }
  }
}
